import {
  getChartsSelections,
  getPlaylistDetail,
  getTracksByIds,
  getRelatedTracks,
  searchAlbums,
  searchPlaylists,
  searchTracks,
  searchUsers,
} from "./api";
import {
  mapAlbumToCard,
  mapPlaylistToCard,
  mapPlaylistToHero,
  mapTrackToCard,
  mapTrackToListRow,
  mapUserToCard,
} from "./mappers";

const HOME_PAGE_LIMIT = 15;
const SEARCH_PAGE_LIMIT = 15;
const SEARCH_SECONDARY_LIMIT = 8;

function normalizeTracksFromPlaylist(playlist) {
  const tracks = Array.isArray(playlist?.tracks) ? playlist.tracks : [];
  return tracks.filter((track) => track && typeof track === "object" && track.id);
}

function normalizeTrackIds(tracks = []) {
  return tracks
    .map((track) => Number(track?.id))
    .filter((id) => Number.isInteger(id) && id > 0);
}

function parseNextOffset(nextHref, fallbackOffset, pageCount) {
  if (!nextHref) return null;

  try {
    const url = new URL(nextHref);
    const value = Number(url.searchParams.get("offset"));
    if (Number.isFinite(value) && value >= 0) return value;
  } catch {
    // Ignore malformed URL and use fallback.
  }

  return fallbackOffset + pageCount;
}

function parsePagination(response, offset, loadedCount, fallbackTotal = 0) {
  return {
    hasMore: Boolean(response?.next_href && loadedCount > 0),
    nextOffset: parseNextOffset(response?.next_href, offset, loadedCount),
    total:
      Number(response?.total_results) ||
      Number(response?.total) ||
      Number(response?.collection?.length) ||
      fallbackTotal ||
      loadedCount,
  };
}

function orderTracksByIds(ids, tracks) {
  const trackMap = new Map((tracks || []).map((track) => [Number(track?.id), track]));
  return ids.map((id) => trackMap.get(Number(id))).filter(Boolean);
}

export async function fetchHomeFeed(options = {}) {
  const limit = Number(options.limit) || HOME_PAGE_LIMIT;
  const offset = Number(options.offset) || 0;

  let cursor = options.cursor || null;
  let heroAlbum = options.heroAlbum || null;
  let relatedTracks = [];

  if (!cursor) {
    const chartsResponse = await getChartsSelections({ limit: 10 });
    const chartGroups = chartsResponse?.collection || [];
    const firstGroup = chartGroups[0];
    const firstPlaylist = firstGroup?.items?.collection?.[0] || null;

    if (!firstPlaylist?.id) {
      throw new Error("No playlist data available from charts selections.");
    }

    const playlistDetail = await getPlaylistDetail(firstPlaylist.id);
    const playlistTracks = normalizeTracksFromPlaylist(playlistDetail);
    const trackIds = normalizeTrackIds(playlistTracks);

    if (!trackIds.length) {
      throw new Error("Trending playlist contains no track IDs.");
    }

    const seedTrackId = trackIds[0];
    try {
      const relatedResponse = await getRelatedTracks(seedTrackId, { limit: 15, offset: 0 });
      relatedTracks = (relatedResponse?.collection || []).map(mapTrackToListRow);
    } catch {
      relatedTracks = playlistTracks.slice(1, 16).map(mapTrackToListRow);
    }

    heroAlbum = mapPlaylistToHero(playlistDetail);
    cursor = {
      playlistId: firstPlaylist.id,
      trackIds,
      seedTrackId,
    };
  }

  const pageTrackIds = (cursor.trackIds || []).slice(offset, offset + limit);
  const fetchedTracks = await getTracksByIds(pageTrackIds);
  const orderedTracks = orderTracksByIds(pageTrackIds, fetchedTracks);

  const nextOffset = offset + pageTrackIds.length;
  const total = cursor.trackIds?.length || 0;
  const hasMore = nextOffset < total;

  return {
    heroAlbum,
    recommendedCards: orderedTracks.map(mapTrackToCard),
    relatedTracks,
    searchCollections: {
      users: [],
      playlists: [],
      albums: [],
    },
    pagination: {
      hasMore,
      nextOffset: hasMore ? nextOffset : null,
      pageSize: orderedTracks.length,
      total,
      cursor,
      collections: {
        users: { hasMore: false, nextOffset: null, total: 0 },
        playlists: { hasMore: false, nextOffset: null, total: 0 },
        albums: { hasMore: false, nextOffset: null, total: 0 },
      },
    },
  };
}

export async function searchHomeFeed(query, options = {}) {
  const normalized = String(query || "").trim();
  const offset = Number(options.offset) || 0;
  const limit = Number(options.limit) || SEARCH_PAGE_LIMIT;
  const includeTracks = options.includeTracks ?? true;
  const includeUsers = options.includeUsers ?? offset === 0;
  const includePlaylists = options.includePlaylists ?? offset === 0;
  const includeAlbums = options.includeAlbums ?? offset === 0;
  const usersOffset = Number(options.usersOffset) || 0;
  const playlistsOffset = Number(options.playlistsOffset) || 0;
  const albumsOffset = Number(options.albumsOffset) || 0;
  const secondaryLimit = Number(options.secondaryLimit) || SEARCH_SECONDARY_LIMIT;

  if (!normalized) {
    return fetchHomeFeed(options);
  }

  const [tracksResponse, usersResponse, playlistsResponse, albumsResponse] = await Promise.all([
    includeTracks
      ? searchTracks(normalized, {
          limit,
          offset,
          linkedPartitioning: 1,
        })
      : Promise.resolve({ collection: [] }),
    includeUsers
      ? searchUsers(normalized, {
          limit: secondaryLimit,
          offset: usersOffset,
          linkedPartitioning: 1,
        })
      : Promise.resolve({ collection: [] }),
    includePlaylists
      ? searchPlaylists(normalized, {
          limit: secondaryLimit,
          offset: playlistsOffset,
          linkedPartitioning: 1,
        })
      : Promise.resolve({ collection: [] }),
    includeAlbums
      ? searchAlbums(normalized, {
          limit: secondaryLimit,
          offset: albumsOffset,
          linkedPartitioning: 1,
        })
      : Promise.resolve({ collection: [] }),
  ]);
  const tracks = tracksResponse?.collection || [];

  const firstTrack = tracks[0];
  const fallbackHeroTitle = options.heroAlbum?.title || `Search: ${normalized}`;
  const fallbackHeroImage =
    options.heroAlbum?.image ||
    "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80";

  const heroAlbum = {
    title: firstTrack?.title || fallbackHeroTitle,
    subtitle: `Live results for "${normalized}" from SoundCloud.`,
    listeners: firstTrack?.user?.username ? `Top result by ${firstTrack.user.username}` : "Search Results",
    duration: `${tracks.length} tracks in this page`,
    image:
      firstTrack?.artwork_url?.replace(/-large\./, "-t500x500.") ||
      firstTrack?.user?.avatar_url?.replace(/-large\./, "-t500x500.") ||
      fallbackHeroImage,
  };

  const recommendedCards = tracks.map(mapTrackToCard);
  const relatedTracks = tracks.map(mapTrackToListRow);
  const tracksPagination = parsePagination(tracksResponse, offset, tracks.length, tracks.length);
  const usersItems = (usersResponse?.collection || []).map(mapUserToCard);
  const playlistsItems = (playlistsResponse?.collection || []).map(mapPlaylistToCard);
  const albumsItems = (albumsResponse?.collection || []).map(mapAlbumToCard);
  const usersPagination = parsePagination(usersResponse, usersOffset, usersItems.length);
  const playlistsPagination = parsePagination(
    playlistsResponse,
    playlistsOffset,
    playlistsItems.length
  );
  const albumsPagination = parsePagination(albumsResponse, albumsOffset, albumsItems.length);

  return {
    heroAlbum,
    recommendedCards,
    relatedTracks,
    searchCollections: {
      users: usersItems,
      playlists: playlistsItems,
      albums: albumsItems,
    },
    pagination: {
      hasMore: tracksPagination.hasMore,
      nextOffset: tracksPagination.nextOffset,
      pageSize: tracks.length,
      total: tracksPagination.total,
      cursor: null,
      collections: {
        users: usersPagination,
        playlists: playlistsPagination,
        albums: albumsPagination,
      },
    },
  };
}
